<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { markRaw } from 'vue';
import { bind, difference, get, isEmpty, isNil, findIndex, pick, isNumber } from 'lodash';
import resources from '@enums/resources';
import vuexForm from '@enums/vuex-form';
import customComponents from '@enums/custom-components';
import valueTypes from '@enums/value-types';
import workflowUtilsFactory from '@sharedModules/workflow-utils-factory';
import featureFlags from '@enums/feature-flags';
import commonFormDialogComponent from './common-form-dialog.vue';
import i18n from '@/js/vue-i18n';
import validators from '@/js/validators';
import { getDefaultValuePropName, getDefaultValuePropValue } from '@/js/utils/component-utils';

const defaultNamespace = 'default';

const {
    canEditSubCampaign,
    canPublishSubCampaigns,
    canReadScenario,
    enableParentChildModule,
    canEditChannel,
    canViewScenariosFrontEnd,
    rateCardsAtSubCampaignLevel,
} = featureFlags;

const { componentTypes } = customComponents;
const { fieldTypes, sourceTypes } = vuexForm;
const { object } = valueTypes;
const { subCampaigns } = resources;

const createForm = function({
    isSubCampaignEditor,
    isChannelEditor,
    isDetailsOpened,
    isNominationsOpened,
    isTagsOpened,
    isRateCardsAtSubCampaignLevel,
    isTotalExpectedPromotionsEnabled,
    defaultTotalExpectedPromotions,
    isChildrenOpened,
    isRateCardsOpened,
    onDetailsPanelStateChange,
    onNominationsPanelStateChange,
    onTagsPanelStateChange,
    onChildrenPanelStateChange,
    onRateCardsPanelStateChange,
    isPrivateDefault,
    subCampaignDefaults,
    workflowTemplateSetter,
    updateWorkflowInstance,
    storeGroupChangeHandler,
    tagsInFilter,
    isParentChildModuleEnabled,
    categoriesTree,
    handleTagUpdate,
    editContext,
    dialogCloseFunction,
    updateCategoriesInStagingArea,
}) {
    return {
        vuexModule: 'subCampaigns',
        editable: isSubCampaignEditor,
        addEvent: 'createSubCampaign',
        editEvent: 'subCampaignUpdated',
        fields: [
            {
                fieldName: 'name',
                editable: isSubCampaignEditor,
                type: fieldTypes.text,
                label: 'planning.createSubCampaign.title',
                placeholder: 'planning.createSubCampaign.titlePlaceholder',
                cssClass: 'vuex-form__name',
                isRequired: true,
                validations: [
                    {
                        message: i18n.t('validation.subCampaign.nameRequired'),
                        validator: validators.required,
                    },
                ],
            },
            {
                type: componentTypes.parentPermalink,
                subCampaignId: get(editContext, '_id', ''),
                dialogCloseFunction,
                cssClass: 'vuex-form__link',
            },
            {
                fieldName: 'briefing',
                editable: isSubCampaignEditor,
                type: fieldTypes.textarea,
                label: 'planning.createSubCampaign.briefing',
                placeholder: 'planning.createSubCampaign.briefingPlaceholder',
                cssClass: 'vuex-form__briefing',
            },
            {
                type: fieldTypes.dateRange,
                label: 'planning.createSubCampaign.dates',
                cssClass: 'vuex-form__dates',
                isRequired: true,
                editable: isSubCampaignEditor,
                minDate: {
                    source: sourceTypes.context,
                    path: 'startDate',
                },
                maxDate: {
                    source: sourceTypes.context,
                    path: 'endDate',
                },
                from: {
                    fieldName: 'startDate',
                    dynamicDefaultValue: {
                        source: sourceTypes.context,
                        path: 'startDate',
                    },
                },

                to: {
                    fieldName: 'endDate',
                    dynamicDefaultValue: {
                        source: sourceTypes.context,
                        path: 'endDate',
                    },
                },
                skipComputedMin: false,
                entity: 'sub-campaign',
                validations: [
                    {
                        message: i18n.t('validation.subCampaign.datesRequired'),
                        validator: validators.required,
                    },
                    {
                        validator: validators.dateRangeRequired,
                    },
                ],
                onChange: value => updateWorkflowInstance(value),
            },
            ...(isTotalExpectedPromotionsEnabled
                ? [
                      {
                          fieldName: 'totalExpectedPromotions',
                          editable: isSubCampaignEditor,
                          type: fieldTypes.number,
                          label: 'planning.createSubCampaign.totalExpectedPromotions',
                          cssClass: 'vuex-form__total-expected-promotions',
                          positiveOnly: true,
                          defaultValue: null,
                          ...(!isNil(defaultTotalExpectedPromotions) && {
                              placeholder: String(defaultTotalExpectedPromotions),
                          }),
                          validations: [
                              {
                                  validator: validators.greaterThanZero,
                              },
                          ],
                      },
                  ]
                : []),
            {
                fieldName: 'workflowTemplateKey',
                fieldPath: 'workflow',
                type: fieldTypes.select,
                cssClass: 'vuex-form__workflow',
                isRequired: true,
                filled: true,
                editable: isSubCampaignEditor,
                label: 'planning.createSubCampaign.workflow',
                placeholder: 'planning.createSubCampaign.workflowPlaceholder',
                options: {
                    source: sourceTypes.getter,
                    identifier: 'getWorkflowTemplateOptions',
                    module: 'clientConfig',
                    params: {},
                },
                setter: workflowTemplateKey => workflowTemplateSetter({ workflowTemplateKey }),
                itemText: 'text',
                itemValue: 'value',
                clearable: true,
                validations: [],
            },
            {
                type: componentTypes.expansionPanel,
                isContainer: true,
                label: 'planning.createSubCampaign.categories',
                cssClass: 'vuex-form__details',
                expansionPanelClass: 'details__wrapper',
                isOpenState: isDetailsOpened,
                change: onDetailsPanelStateChange,
                editable: true,
                elvation: 0,
                flat: true,
                children: [
                    {
                        fieldName: 'campaignId',
                        dynamicDefaultValue: {
                            source: sourceTypes.context,
                            path: '_id',
                        },
                    },
                    {
                        fieldName: 'resourceDefinitions',
                        defaultValue: [],
                    },
                    {
                        fieldName: 'categories',
                        type: componentTypes.categoriesSelect,
                        tree: categoriesTree,
                        cssClass: 'vuex-form__categories',
                        editable: isSubCampaignEditor,
                        isRequired: true,
                        update: updateCategoriesInStagingArea,
                        [getDefaultValuePropName(
                            subCampaignDefaults.categories
                        )]: getDefaultValuePropValue(subCampaignDefaults.categories, {
                            source: sourceTypes.context,
                            path: 'categories',
                        }),
                    },
                    {
                        fieldName: 'customerGroups',
                        type: fieldTypes.checkboxesList,
                        cssClass: 'vuex-form__customer-restrictions',
                        isRequired: true,
                        editable: isSubCampaignEditor,
                        label: 'planning.customerRestrictions',
                        options: {
                            source: sourceTypes.getter,
                            identifier: 'getCheckboxListOptions',
                            module: 'subCampaigns',
                            params: {
                                promotionAttributeName: 'offerMechanic',
                                promotionAttributeKey: 'customerAvailability',
                                campaignAttributeName: 'customerGroups',
                                campaignAttributeKey: 'key',
                                resource: 'clientConfig',
                                getOptionsFunction: 'getCustomerRestrictions',
                                campaignId: {
                                    source: sourceTypes.context,
                                    path: '_id',
                                },
                                subCampaignId: {
                                    source: sourceTypes.editContext,
                                    path: '_id',
                                },
                            },
                        },
                        itemText: 'description',
                        itemValue: 'key',
                        valueType: object,
                        [getDefaultValuePropName(
                            subCampaignDefaults.customerRestrictions
                        )]: getDefaultValuePropValue(subCampaignDefaults.customerRestrictions, {
                            source: sourceTypes.context,
                            path: 'customerGroups',
                        }),
                        validations: [
                            {
                                validator: validators.required,
                            },
                        ],
                    },
                ],
            },
            {
                type: componentTypes.expansionPanel,
                editable: true,
                elvation: 0,
                flat: true,
                isContainer: true,
                label: 'planning.createSubCampaign.resources',
                cssClass: 'vuex-form__nominations',
                isOpenState: isNominationsOpened,
                change: onNominationsPanelStateChange,
                children: [
                    {
                        type: componentTypes.nominationTabs,
                        createMode: true,
                        parentContextType: subCampaigns,
                        onResourceChange: updateWorkflowInstance,
                        onStoreGroupChange: isRateCardsAtSubCampaignLevel
                            ? storeGroupChangeHandler
                            : () => {},
                        storeGroups: {
                            fieldName: 'storeGroups',
                            editable: isSubCampaignEditor,
                            options: {
                                source: sourceTypes.getter,
                                identifier: 'getCheckboxListOptions',
                                module: 'subCampaigns',
                                params: {
                                    promotionAttributeName: 'storeGroups',
                                    promotionAttributeKey: 'key',
                                    resource: 'storeGroups',
                                    getOptionsFunction: 'getAvailableStoreGroupsOptions',
                                    startDate: {
                                        source: sourceTypes.editContext,
                                        path: 'startDate',
                                    },
                                    selectedStoreGroups: {
                                        source: sourceTypes.editContext,
                                        path: 'storeGroups',
                                    },
                                    getUserAccessOptionsMap: 'userStoreGroupsMap',
                                    campaignId: {
                                        source: sourceTypes.context,
                                        path: '_id',
                                    },
                                    subCampaignId: {
                                        source: sourceTypes.editContext,
                                        path: '_id',
                                    },
                                },
                            },
                            itemText: 'description',
                            itemValue: 'key',
                            valueType: object,
                            [getDefaultValuePropName(
                                subCampaignDefaults.storeGroups
                            )]: getDefaultValuePropValue(subCampaignDefaults.storeGroups, {
                                source: sourceTypes.context,
                                path: 'storeGroups',
                            }),
                        },
                        resources: {
                            isRequired: true,
                            fieldName: 'resources',
                            // Resources can be updated by users who have permission to
                            // update a sub-campaign, or by users who have explicit
                            // permission to update channels.
                            editable: isSubCampaignEditor || isChannelEditor,
                            itemText: 'description',
                            itemValue: 'key',
                            options: {
                                source: sourceTypes.getter,
                                identifier: 'getResourceOptions',
                                module: 'subCampaigns',
                                params: {
                                    parentModule: 'campaigns',
                                    parentGetter: 'getCampaignById',
                                    parentContextId: {
                                        source: sourceTypes.context,
                                        path: '_id',
                                    },
                                    contextId: {
                                        source: sourceTypes.editContext,
                                        path: '_id',
                                    },
                                    contextModule: 'subCampaigns',
                                    contextGetter: 'getSubCampaignById',
                                    childModule: 'promotions',
                                    childGetter: 'allPromotionsBySubCampaignId',
                                },
                            },
                            [getDefaultValuePropName(
                                subCampaignDefaults.promoResources
                            )]: getDefaultValuePropValue(subCampaignDefaults.promoResources, {
                                source: sourceTypes.getter,
                                identifier: 'getDefaultResources',
                                module: 'subCampaigns',
                                params: {
                                    resources: {
                                        source: sourceTypes.context,
                                        path: 'promoResources',
                                    },
                                    storeGroups: {
                                        source: sourceTypes.context,
                                        path: 'storeGroups',
                                    },
                                },
                            }),
                        },
                        defaultStoreGroups: {
                            fieldName: 'defaultStoreGroups',
                            editable: isSubCampaignEditor,
                            options: {
                                source: sourceTypes.getter,
                                identifier: 'getSelectedSubCampaignStoreGroupOptions',
                                module: 'subCampaigns',
                                params: {
                                    subCampaignId: {
                                        source: sourceTypes.editContext,
                                        path: '_id',
                                    },
                                },
                            },
                            itemText: 'description',
                            itemValue: 'key',
                            valueType: object,
                        },
                        defaultResources: {
                            isRequired: true,
                            fieldName: 'defaultResources',
                            // Resources can be updated by users who have permission to
                            // update a sub-campaign, or by users who have explicit
                            // permission to update channels.
                            editable: isSubCampaignEditor || isChannelEditor,
                            itemText: 'description',
                            itemValue: 'key',
                            options: {
                                source: sourceTypes.getter,
                                identifier: 'getSelectedSubCampaignResourceOptions',
                                module: 'subCampaigns',
                                params: {
                                    subCampaignId: {
                                        source: sourceTypes.editContext,
                                        path: '_id',
                                    },
                                },
                            },
                        },
                    },
                ],
            },
            {
                type: componentTypes.expansionPanel,
                editable: true,
                elvation: 0,
                flat: true,
                isContainer: true,
                label: 'planning.createSubCampaign.tags',
                cssClass: 'vuex-form__tags',
                isOpenState: isTagsOpened,
                change: onTagsPanelStateChange,
                children: [
                    {
                        fieldName: 'tags',
                        editable: isSubCampaignEditor,
                        type: fieldTypes.comboBox,
                        placeholder: 'planning.createSubCampaign.tagsPlaceholder',
                        options: {
                            source: sourceTypes.getter,
                            identifier: 'getCheckboxListOptions',
                            module: 'subCampaigns',
                            params: {
                                promotionAttributeName: 'tags',
                                promotionAttributeKey: 'tagKey',
                                resource: 'tagMetadata',
                                getOptionsFunction: 'getTagOptions',
                                subCampaignId: {
                                    source: sourceTypes.editContext,
                                    path: '_id',
                                },
                            },
                        },
                        multiple: true,
                        chips: true,
                        deletableChips: true,
                        returnObject: true,
                        density: 'compact',
                        itemValue: 'tagKey',
                        itemText: 'tagName',
                        createAction: 'tagMetadata/createTag',
                        deleteAction: tag => handleTagUpdate('delete', tag),
                        updateAction: tag => handleTagUpdate('update', tag),
                    },
                ],
            },
            {
                type: componentTypes.expansionPanel,
                editable: true,
                elvation: 0,
                flat: true,
                isContainer: true,
                label: 'planning.createSubCampaign.children',
                cssClass: 'vuex-form__children',
                isOpenState: isChildrenOpened,
                change: onChildrenPanelStateChange,
                omitField: !isParentChildModuleEnabled,
                children: [
                    {
                        fieldName: 'children',
                        chips: true,
                        editable: isSubCampaignEditor,
                        type: fieldTypes.select,
                        placeholder: 'planning.createSubCampaign.childrenPlaceholder',
                        multiple: true,
                        autocomplete: true,
                        density: 'compact',
                        itemText: 'name',
                        itemValue: 'id',
                        options: {
                            source: sourceTypes.getter,
                            identifier: 'getSubCampaignChildOptions',
                            module: 'subCampaigns',
                            params: {
                                subCampaignId: {
                                    source: sourceTypes.editContext,
                                    path: '_id',
                                },
                            },
                        },
                    },
                ],
            },
            ...(!isRateCardsAtSubCampaignLevel
                ? []
                : [
                      {
                          type: componentTypes.expansionPanel,
                          editable: true,
                          elvation: 0,
                          flat: true,
                          isContainer: true,
                          label: 'planning.createSubCampaign.rateCards',
                          cssClass: 'vuex-form__rate-cards',
                          isOpenState: isRateCardsOpened,
                          change: onRateCardsPanelStateChange,
                          children: [
                              {
                                  fieldName: 'rateCards',
                                  chips: true,
                                  editable: isSubCampaignEditor,
                                  type: fieldTypes.select,
                                  placeholder: 'planning.createSubCampaign.rateCardsPlaceholder',
                                  multiple: true,
                                  autocomplete: true,
                                  density: 'compact',
                                  itemText: 'name',
                                  itemValue: 'id',
                                  options: {
                                      source: sourceTypes.getter,
                                      identifier: 'getRateCardsOptions',
                                      module: 'subCampaigns',
                                      params: {
                                          subCampaignId: {
                                              source: sourceTypes.editContext,
                                              path: '_id',
                                          },
                                      },
                                  },
                              },
                          ],
                      },
                  ]),
            {
                fieldName: 'isPrivate',
                defaultValue: isPrivateDefault,
                editable: isSubCampaignEditor,
            },
            {
                fieldName: 'resourceDefinitions',
                defaultValue: [],
                // Resource definitions are updated to align with any updates
                // that are made to resources, therefore both resources and
                // resourceDefinitions need to have the same 'editable' value
                // to ensure they both exist on the form.
                editable: isSubCampaignEditor || isChannelEditor,
            },
            {
                fieldName: 'workflowState',
                defaultValue: [],
            },
            {
                fieldName: 'workflow',

                editable: isSubCampaignEditor,
            },
            {
                fieldName: 'tags',
                defaultValue: tagsInFilter,
                editable: isSubCampaignEditor,
            },
            {
                fieldName: 'children',
                editable: isSubCampaignEditor,
                defaultValue: [],
            },
            {
                fieldName: 'rateCards',
                editable: isSubCampaignEditor,
                defaultValue: [],
            },
        ],
    };
};

export default {
    extends: commonFormDialogComponent,

    preparationView: {
        type: Boolean,
        default: false,
    },
    emits: ['sub-campaign-selected', 'change'],

    data: () => ({
        resource: subCampaigns,
        btnCancelId: 'subCampaignCancelId',
        btnConfirmDialogCancel: 'subCampaignConfirmDialogCancelId',
        btnConfirmId: 'subCampaignBtnConfirmId',
        isDetailsOpened: null,
        isNominationsOpened: null,
        isTagsOpened: null,
        isChildrenOpened: null,
        isRateCardsOpened: null,
        dialogQuestionText: 'dialogs.deleteChildSubCampaign.header',
        dialogActionText: 'actions.unlink',
        dialogComponentName: 'delete-children-warning',
        dialogContext: {},
        dialogFieldName: 'children',
        hasConfirmationDialog: true,
        dialogConfirmFunction: null,
        dialogProps: {},
    }),

    computed: {
        ...mapState('clientConfig', ['toggleLogic', 'additionalDetailsDefaultSelection']),
        ...mapGetters('subCampaigns', [
            'getStagingAreaField',
            'getSubCampaignChildOptions',
            'getSubCampaignNamesByIds',
            'getPromotionCategoriesBySubCampaignId',
            'isTotalExpectedPromotionsEnabled',
            'defaultTotalExpectedPromotions',
        ]),
        ...mapGetters('clientConfig', ['getWorkflowTemplateByKey']),
        ...mapGetters('tagMetadata', ['getTagsInFilter']),
        ...mapGetters('hierarchy', ['getCategoryKeysForHierarchyNode', 'getCategoriesTree']),

        form() {
            return markRaw(
                createForm({
                    isSubCampaignEditor: !!this.toggleLogic[canEditSubCampaign],
                    isChannelEditor: !!this.toggleLogic[canEditChannel],
                    isRateCardsAtSubCampaignLevel: this.toggleLogic[rateCardsAtSubCampaignLevel],
                    isTotalExpectedPromotionsEnabled: this.isTotalExpectedPromotionsEnabled,
                    defaultTotalExpectedPromotions: this.defaultTotalExpectedPromotions,
                    isDetailsOpened: this.isDetailsOpened,
                    isNominationsOpened: this.isNominationsOpened,
                    isTagsOpened: this.isTagsOpened,
                    isChildrenOpened: this.isChildrenOpened,
                    isRateCardsOpened: this.isRateCardsOpened,
                    onDetailsPanelStateChange: bind(
                        this.onExpansionPanelStateChange,
                        this,
                        'isDetailsOpened'
                    ),
                    onNominationsPanelStateChange: bind(
                        this.onExpansionPanelStateChange,
                        this,
                        'isNominationsOpened'
                    ),
                    onChildrenPanelStateChange: bind(
                        this.onExpansionPanelStateChange,
                        this,
                        'isChildrenOpened'
                    ),
                    onRateCardsPanelStateChange: bind(
                        this.onExpansionPanelStateChange,
                        this,
                        'isRateCardsOpened'
                    ),
                    isPrivateDefault: !!this.toggleLogic[canPublishSubCampaigns],
                    subCampaignDefaults: this.additionalDetailsDefaultSelection.subCampaign,
                    workflowTemplateSetter: this.workflowTemplateSetter,
                    updateWorkflowInstance: this.updateWorkflowInstance,
                    storeGroupChangeHandler: this.storeGroupChangeHandler,
                    tagsInFilter: this.getTagsInFilter,
                    isParentChildModuleEnabled: !!this.toggleLogic[enableParentChildModule],
                    handleTagUpdate: this.handleTagUpdate,
                    editContext: this.editContext,
                    dialogCloseFunction: this.close,
                    updateCategoriesInStagingArea: this.updateCategoriesInStagingArea,
                    categoriesTree: this.getCategoriesTree({
                        parentCategories: this.context.categories,
                        childCategories: this.getPromotionCategoriesBySubCampaignId({
                            subCampaignId: get(this.editContext, '_id'),
                        }),
                        selectedCategories: this.getStagingAreaField({
                            namespace: this.editContext ? this.editContext._id : 'default',
                            fieldName: 'categories',
                        }),
                        isEditable: !!this.toggleLogic[canEditSubCampaign] && !this.isReadOnly,
                    }),
                })
            );
        },
        labels() {
            return {
                btnText: 'planning.addSubCampaign',
                heading: this.editMode
                    ? 'planning.editSubCampaign.heading'
                    : 'planning.createSubCampaign.heading',
                hasBorder: true,
                planCampaign: 'planning.planSubCampaign',
            };
        },
    },

    methods: {
        ...mapActions('subCampaigns', ['setStagingAreaField', 'refreshSubCampaigns']),
        ...mapActions('rateCards', ['fetchRateCardsByStoreGroupsAndDates']),

        planOutCampaign() {
            this.selectCampaignSingleView();

            if (this.toggleLogic[canViewScenariosFrontEnd]) {
                this.viewScenarios();
            }
        },

        setModalExpansionPanelsState() {
            this.setDetailsPanelState();
            this.setNominationsPanelState();
            this.setTagsPanelState();
            this.setChildrenPanelState();
            this.setRateCardsPanelState();
        },

        setDetailsPanelState() {
            this.setExpansionPanelState('isDetailsOpened', ['customerGroups', 'categories']);
        },

        setNominationsPanelState() {
            this.setExpansionPanelState('isNominationsOpened', ['storeGroups', 'resources']);
        },

        setTagsPanelState() {
            this.setExpansionPanelState('isTagsOpened', ['tags']);
        },

        setChildrenPanelState() {
            this.setExpansionPanelState('isChildrenOpened', ['children']);
        },
        setRateCardsPanelState() {
            this.setExpansionPanelState('isRateCardsOpened', ['rateCards']);
        },

        closeModal() {
            this.close();
        },

        viewScenarios() {
            this.close();
            if (this.toggleLogic[canReadScenario]) {
                this.$emit('sub-campaign-selected', this.editContext);
            }
        },

        async handleConfirm() {
            if (this.editContext) {
                const subCampaign = this.$store.state[this.entityType].stagingArea[
                    this.editContext._id
                ];

                const deletedChildrenIds = difference(
                    this.editContext.children,
                    subCampaign.children
                );

                if (!isEmpty(deletedChildrenIds)) {
                    this.dialogContext[this.dialogFieldName] = this.getSubCampaignNamesByIds(
                        deletedChildrenIds
                    );
                    this.$refs['confirmation-dialog'].open();
                    this.$refs['confirmation-dialog'].confirm = async () => {
                        this.$refs['confirmation-dialog'].close();
                        await this.saveEntity();
                        await this.refreshSubCampaigns({ subCampaignIds: deletedChildrenIds });
                    };
                } else {
                    await this.saveEntity();
                    const children = get(subCampaign, 'children', []);
                    await this.refreshSubCampaigns({ subCampaignIds: children });
                }
            } else {
                const childrenAdded = get(
                    this.$store.state[this.entityType].stagingArea.default,
                    'children',
                    []
                );
                await this.saveEntity();
                if (!isEmpty(childrenAdded)) {
                    await this.refreshSubCampaigns({ subCampaignIds: childrenAdded });
                }
            }
        },

        async handleTagUpdate(action, tag) {
            const isDelete = action === 'delete';

            // In case we edit tags we may end with two tags with the same name,
            // so we check if tags with the same name exist.
            let tagDuplicate;
            if (!isDelete) {
                // we expect to only have 1 or less matching tag names
                tagDuplicate = (await this.$store.dispatch('tagMetadata/fetchTagMetadata', {
                    params: {
                        where: {
                            _id: { $ne: tag.id },
                            tagName: { $regex: `^${tag.tagName}$`, $options: 'i' },
                            $or: [{ isDeleted: { $eq: false } }, { isDeleted: { $exists: false } }],
                        },
                    },
                    returnDocuments: true,
                }))[0];
            }

            const dependencies = await this.$store.dispatch('tagMetadata/fetchChildDependencies', {
                // fetching dependencies that will be influenced by tag update or delete
                id: tag.id,
            });

            if (!isEmpty(tagDuplicate)) {
                // case of merging two tags.
                this.dialogComponentName = 'update-tag-warning';
                this.dialogContext = 'tagMetadata';
                this.dialogFieldName = 'childDependencies';
                this.dialogQuestionText = 'dialogs.mergeTags.header';
                this.dialogActionText = 'actions.update';
                this.dialogConfirmFunction = async () => {
                    await this.mergeTags(tag, tagDuplicate);
                };
                this.dialogProps = {
                    mergeTags: true,
                    tagName: tag.tagName,
                    duplicateTagName: tagDuplicate.tagName,
                    childDependencies: dependencies,
                    warningText: 'dialogs.mergeTags.warning',
                    descriptionText: 'dialogs.mergeTags.description',
                };
                this.$refs['confirmation-dialog'].open();
            } else if (!isEmpty(dependencies)) {
                // case of updating or deleting tag with dependencies.
                this.dialogComponentName = 'update-tag-warning';
                this.dialogContext = 'tagMetadata';
                this.dialogFieldName = 'childDependencies';
                this.dialogQuestionText = isDelete
                    ? 'dialogs.deleteTag.header'
                    : 'dialogs.changeTagName.header';
                this.dialogActionText = isDelete ? 'actions.delete' : 'actions.update';
                this.dialogConfirmFunction = async () => {
                    if (isDelete) {
                        await this.deleteTag(tag);
                    } else {
                        await this.updateTag(tag);
                    }
                };
                this.dialogProps = {
                    childDependencies: dependencies,
                    warningText: isDelete
                        ? 'dialogs.deleteTag.warning'
                        : 'dialogs.changeTagName.warning',
                    ...(isDelete ? { tagName: tag.tagName, deleteTag: true } : {}),
                };
                this.$refs['confirmation-dialog'].open();
            } else if (isDelete) {
                // Cases of updating or deleting tag without dependencies.
                await this.deleteTag(tag);
            } else {
                await this.updateTag(tag);
            }
        },

        workflowTemplateSetter({ workflowTemplateKey }) {
            let workflowInstance = {};
            const namespace = this.editContext ? this.editContext._id : defaultNamespace;

            if (workflowTemplateKey) {
                const workflowTemplate = this.getWorkflowTemplateByKey(workflowTemplateKey);

                const subCampaign = this.$store.state[this.entityType].stagingArea[namespace];
                const workflowUtils = workflowUtilsFactory(this.$moment);
                const getCategoryKeysForHierarchyNode = this.getCategoryKeysForHierarchyNode;

                workflowInstance = workflowUtils.convertWorkflowTemplateToInstance({
                    workflowTemplate,
                    subCampaign,
                    getCategoryKeysForHierarchyNode,
                });
            }

            this.setStagingAreaField({
                namespace,
                fieldName: 'workflow',
                value: workflowInstance,
            });
        },
        async storeGroupChangeHandler(storeGroups) {
            await this.fetchRateCardsByStoreGroupsAndDates({
                subCampaignDetails: {
                    storeGroups,
                    startDate: this.getStagingAreaField({
                        namespace: this.editContext ? this.editContext._id : defaultNamespace,
                        fieldName: 'startDate',
                    }),
                    endDate: this.getStagingAreaField({
                        namespace: this.editContext ? this.editContext._id : defaultNamespace,
                        fieldName: 'endDate',
                    }),
                },
            });
        },
        updateWorkflowInstance() {
            const namespace = this.editContext ? this.editContext._id : defaultNamespace;
            const workflow = this.getStagingAreaField({
                namespace,
                fieldName: 'workflow',
            });

            if (workflow && workflow.workflowTemplateKey) {
                this.workflowTemplateSetter({ workflowTemplateKey: workflow.workflowTemplateKey });
            }
        },

        async deleteTag(tag) {
            await this.$store.dispatch('tagMetadata/deleteTagMetadata', { id: tag.id });
            this.updateTagsInStagingArea({ action: 'delete', tag });
            this.$refs['confirmation-dialog'].close();
            this.resetConfirmDialogConfig();
        },

        async updateTag(tag) {
            // sub-campaign to refetch after tag update
            const namespace = this.editContext ? this.editContext._id : defaultNamespace;
            await this.$store.dispatch('tagMetadata/updateTagName', { tag, namespace });
            this.updateTagsInStagingArea({ action: 'update', tag });
            this.$refs['confirmation-dialog'].close();
            this.resetConfirmDialogConfig();
        },

        async mergeTags(tag, tagDuplicate) {
            // sub-campaign to refetch after tag merge
            const namespace = this.editContext ? this.editContext._id : defaultNamespace;
            await this.$store.dispatch('tagMetadata/mergeTags', {
                tag,
                tagDuplicate,
                namespace,
            });
            this.mergeTagsInStagingArea({ tag, tagDuplicate });
            this.$refs['confirmation-dialog'].close();
            this.resetConfirmDialogConfig();
        },

        mergeTagsInStagingArea({ tag, tagDuplicate }) {
            const namespace = this.editContext ? this.editContext._id : defaultNamespace;
            const tags = this.$store.state[this.entityType].stagingArea[namespace].tags;
            const index = findIndex(tags, { tagKey: tag.tagKey });
            // delete tag (with old name) and duplicate tag
            // then, if tag or duplicate tag are present, push in tag but with a new name
            if (isNumber(index)) {
                tags.splice(index, 1);
            }
            const indexDuplicate = findIndex(tags, { tagKey: tagDuplicate.tagKey });
            if (isNumber(indexDuplicate)) {
                tags.splice(indexDuplicate, 1);
            }
            if (isNumber(index) || isNumber(indexDuplicate)) {
                tags.push(tag);
            }
        },

        updateTagsInStagingArea({ action, tag }) {
            const namespace = this.editContext ? this.editContext._id : defaultNamespace;
            const tags = this.$store.state[this.entityType].stagingArea[namespace].tags;
            const index = findIndex(tags, { tagKey: tag.tagKey });
            if (index > -1) {
                if (action === 'delete') {
                    tags.splice(index, 1);
                } else {
                    tags.splice(index, 1, pick(tag, ['tagKey', 'tagName']));
                }
            }
            this.$emit('change');
        },

        updateCategoriesInStagingArea(selectedCategories) {
            const namespace = this.editContext ? this.editContext._id : 'default';

            this.setStagingAreaField({
                namespace,
                fieldName: 'categories',
                value: selectedCategories,
            });
            this.updateWorkflowInstance();
        },

        resetConfirmDialogConfig() {
            this.dialogComponentName = 'delete-children-warning';
            this.dialogContext = {};
            this.dialogFieldName = 'children';
            this.dialogQuestionText = 'dialogs.deleteChildSubCampaign.header';
            this.dialogActionText = 'actions.unlink';
            this.dialogConfirmFunction = null;
            this.dialogProps = {};
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.campaign-container {
    color: $promo-text-colour;
    width: 89rem;

    :deep(.vuex-form) {
        display: grid;
        grid-template:
            'title description dates'
            'link description workflow'
            '. description total-expected-promotions'
            'details details details'
            'store-groups store-groups store-groups'
            'nominations nominations nominations '
            'tags tags tags'
            'children children children'
            'rate-cards rate-cards rate-cards'
            / 1fr 1fr 1fr;
    }

    :deep(.vuex-form__total-expected-promotions) {
        grid-area: total-expected-promotions;
        padding-bottom: 1rem;
    }

    :deep(.vuex-form__dates) {
        grid-area: dates;
        padding-top: 1rem;
    }

    :deep(.vuex-form__link) {
        grid-area: link;
    }

    :deep(.vuex-form__workflow) {
        grid-area: workflow;
    }

    :deep(.vuex-form__details) {
        grid-area: details;
        border-top: $component-border-divider;
        margin: 0 !important;

        .details {
            &__wrapper {
                display: grid;
                grid-template:
                    'categories . . regions'
                    'promo-resources custom-restrictions . .';
            }
        }
    }

    :deep(.vuex-form__nominations) {
        grid-area: nominations;
        border-top: $component-border-divider;
        margin: 0 !important;
        padding-bottom: 1rem;
    }

    :deep(.vuex-form__tags) {
        grid-area: tags;
        border-top: $component-border-divider;
        margin: 0 !important;
        padding-bottom: 1rem;
    }

    :deep(.vuex-form__children) {
        grid-area: children;
        border-top: $component-border-divider;
        margin: 0 !important;
        padding-bottom: 1rem;
    }

    :deep(.vuex-form__rate-cards) {
        grid-area: rate-cards;
        border-top: $component-border-divider;
        margin: 0 !important;
        padding-bottom: 1rem;
    }

    :deep(.vuex-form__name) {
        grid-area: title;
        padding-right: 2rem;
        width: 26.5rem;
    }

    :deep(.vuex-form__briefing) {
        grid-area: description;
        padding-right: 2rem;
        width: 38.5rem;

        .v-textarea {
            .v-field__input {
                border: 1px solid #e2e2e2;
                font-size: 1.2rem;
                padding: 1.1rem 1.3rem 1.1rem 1rem;
                opacity: 1;
                mask-image: none;

                &:focus {
                    border-color: #969696;
                }

                &::placeholder {
                    color: #969696;
                    font-size: 1.2rem;
                    opacity: 1;
                }
            }
            .v-field__overlay,
            .v-field__outline {
                display: none;
            }
        }
    }

    :deep(.vuex-form) {
        .rtls-expansion-panel-content {
            .v-expansion-panel-text__wrapper {
                display: contents;
            }
        }
    }

    :deep(.vuex-form__store-groups) {
        grid-area: store-groups;
        border-top: $component-border-divider;
        margin: 0 !important;
        padding-bottom: 1rem;
    }

    :deep(.vuex-form__store-groups-selector) {
        display: flex;
        min-width: 50%;
        max-width: 100%;
    }

    :deep(.vuex-form__promo-resources) {
        grid-area: promo-resources;
        padding-bottom: 1.4rem;

        .v-selection-control__wrapper {
            max-width: 3.3rem;
        }

        .icon-checkboxes-list-container__control {
            padding: 0;
            display: flex;
            position: relative;
            border-right: $component-border-divider;

            .v-input--checkbox.error--text {
                padding-bottom: 1rem;
            }

            .v-messages.error--text {
                position: absolute;
                top: 2.5rem;
                left: 0;
            }
        }
    }

    :deep(.vuex-form__categories) {
        grid-area: categories;
        grid-column: 1 / span 2;

        .select-all {
            .v-selection-control__wrapper {
                width: 2.4rem;
            }
        }
    }

    :deep(.vuex-form__customer-restrictions) {
        grid-area: custom-restrictions;
        padding: 0;

        .v-selection-control__wrapper {
            width: 2.4rem;
        }

        .checkboxes-list-container__control {
            display: flex;
            position: relative;

            .v-messages.error--text {
                position: absolute;
                top: 2rem;
                left: 0;
            }

            .rtls-checkbox {
                margin-right: 1.5rem;
            }
        }
    }

    &__bottom-container {
        display: flex;
        align-items: center;

        button {
            height: 2rem !important;
        }

        .author-container {
            display: flex;
            justify-content: space-between;
            line-height: 2.1rem;
            font-size: 1.2rem;
            font-weight: 600;

            & > p {
                margin-right: 1.3rem;
            }
        }
        .v-divider {
            margin-right: 1.3rem;
            margin-left: 1.3rem;
        }

        &-right {
            display: flex;
            margin-left: auto;
        }
    }

    &__author-avatar {
        background-color: $promo-text-colour;
        font-size: 1rem;
        font-weight: 400;
    }

    &__last-updated {
        font-size: 1.2rem;
        font-weight: 600;
        .date {
            font-weight: 400;
        }
    }

    .white--text {
        color: $promo-white;
    }

    :deep(.v-expansion-panel-title:hover > .v-expansion-panel-title__overlay) {
        background-color: $promo-white;
    }
}
</style>
